import { useSnackbar } from 'notistack';

import type { Resource } from '@setvi/shared/interfaces';
import { useDialog } from '@setvi/shared/providers';

import { CustomizeDialog } from '../components/customize-dialog';
import { LocationDialog } from '../components/location-dialog';
import { CUSTOMIZE_DIALOG_ID, LOCATION_DIALOG_ID } from '../constants';
import { usePrintOrderStore } from '../store';
import { usePrintOrderCart } from './use-print-order-cart';
import { usePrintOrderEnabled } from './use-print-order-enabled';

const CANNOT_ADD_TOOLTIP = 'This resource is not available to add to cart';

interface UsePrintOrderCartActionOptions {
  /** Work the caller has in flight, folded into the action's busy state. */
  busy?: boolean;
  /** Runs once an add or remove has settled. */
  onDone?: () => void;
  /**
   * Runs when one of the customization dialogs takes over, before it opens, so
   * the caller can get out of its way. Defaults to `onDone`.
   */
  onHandOff?: () => void;
}

/**
 * The single Add to Cart / Remove from Cart behaviour, for every surface that
 * offers it (the resource dropdown, the preview dialog).
 *
 * An asset is never added blind: one that takes a location or a QR sheet /
 * cover page opens its own dialog, which adds it once the user submits. Only
 * an asset with no customization goes straight into the cart.
 */
export const usePrintOrderCartAction = (
  resource: Resource,
  {
    busy = false,
    onDone,
    onHandOff = onDone
  }: UsePrintOrderCartActionOptions = {}
) => {
  const enabled = usePrintOrderEnabled();
  const isInCart = usePrintOrderStore(state =>
    state.items.some(item => item.resource.ID === resource.ID)
  );
  const { addItem, removeItem, isLoading } = usePrintOrderCart();
  const { openDialog, closeDialog } = useDialog();
  const { enqueueSnackbar } = useSnackbar();

  const loading = isLoading || busy;
  // Removing stays allowed even for a resource that can no longer be added.
  const cannotAdd = !isInCart && resource.EnableAddToCart === false;

  const openInDialog = (dialog: JSX.Element, id: string) => {
    onHandOff?.();
    openDialog(dialog, { id });
  };

  const toggle = async () => {
    // Server first, then locally. The mutation cache surfaces any error toast,
    // so only success is announced here.
    if (isInCart) {
      try {
        await removeItem(resource.ID);
        enqueueSnackbar('Removed from cart.', { variant: 'success' });
      } catch {
        // error toast handled globally by the mutation cache
      } finally {
        onDone?.();
      }
      return;
    }

    if (resource.HasLocation) {
      openInDialog(
        <LocationDialog
          resource={resource}
          onClose={() => closeDialog(LOCATION_DIALOG_ID)}
        />,
        LOCATION_DIALOG_ID
      );
      return;
    }

    if (resource.HasQRCode || resource.HasCoverPage) {
      openInDialog(
        <CustomizeDialog
          resource={resource}
          onClose={() => closeDialog(CUSTOMIZE_DIALOG_ID)}
        />,
        CUSTOMIZE_DIALOG_ID
      );
      return;
    }

    try {
      await addItem(resource);
      enqueueSnackbar('Added to cart.', { variant: 'success' });
    } catch {
      // error toast handled globally by the mutation cache
    } finally {
      onDone?.();
    }
  };

  return {
    /** False where the print-order feature does not belong on the screen. */
    enabled,
    isInCart,
    label: isInCart ? 'Remove from Cart' : 'Add to Cart',
    loading,
    disabled: loading || cannotAdd,
    tooltip: cannotAdd ? CANNOT_ADD_TOOLTIP : '',
    toggle
  };
};

export type PrintOrderCartAction = ReturnType<typeof usePrintOrderCartAction>;
